/**
 * WordPress dependencies
 */
import { useEffect, useRef, useCallback } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';

/**
 * Internal dependencies
 */
import { getBlockClientId, getBlockDOMNode } from '../../utils/dom';
import { store as blockEditorStore } from '../../store';

/**
 * Returns for the deepest node at the start or end of a container node. Ignores
 * any text nodes that only contain HTML formatting whitespace.
 *
 * @param {Element} node Container to search.
 * @param {string} type 'start' or 'end'.
 */
function getDeepestNode( node, type ) {
	const child = type === 'start' ? 'firstChild' : 'lastChild';
	const sibling = type === 'start' ? 'nextSibling' : 'previousSibling';

	while ( node[ child ] ) {
		node = node[ child ];

		while (
			node.nodeType === node.TEXT_NODE &&
			/^[ \t\n]*$/.test( node.data ) &&
			node[ sibling ]
		) {
			node = node[ sibling ];
		}
	}

	return node;
}

function selector( select ) {
	const {
		isSelectionEnabled,
		isMultiSelecting,
		getMultiSelectedBlockClientIds,
		hasMultiSelection,
		getSelectedBlockClientId,
	} = select( blockEditorStore );

	return {
		isSelectionEnabled: isSelectionEnabled(),
		isMultiSelecting: isMultiSelecting(),
		multiSelectedBlockClientIds: getMultiSelectedBlockClientIds(),
		hasMultiSelection: hasMultiSelection(),
		selectedBlockClientId: getSelectedBlockClientId(),
	};
}

function toggleRichText( container, toggle ) {
	Array.from( container.querySelectorAll( '.rich-text' ) ).forEach(
		( node ) => {
			if ( toggle ) {
				node.setAttribute( 'contenteditable', true );
			} else {
				node.removeAttribute( 'contenteditable' );
			}
		}
	);
}

export default function useMultiSelection( ref ) {
	const {
		isSelectionEnabled,
		isMultiSelecting,
		multiSelectedBlockClientIds,
		hasMultiSelection,
		selectedBlockClientId,
	} = useSelect( selector, [] );
	const { getBlockParents } = useSelect( blockEditorStore );
	const {
		startMultiSelect,
		stopMultiSelect,
		multiSelect,
		selectBlock,
	} = useDispatch( blockEditorStore );
	const rafId = useRef();
	const startClientId = useRef();
	const anchorElement = useRef();

	/**
	 * When the component updates, and there is multi selection, we need to
	 * select the entire block contents.
	 */
	useEffect( () => {
		const { ownerDocument } = ref.current;
		const { defaultView } = ownerDocument;

		if ( ! hasMultiSelection || isMultiSelecting ) {
			if ( ! selectedBlockClientId || isMultiSelecting ) {
				return;
			}

			const selection = defaultView.getSelection();

			if ( selection.rangeCount && ! selection.isCollapsed ) {
				const blockNode = getBlockDOMNode(
					selectedBlockClientId,
					ownerDocument
				);
				const { startContainer, endContainer } = selection.getRangeAt(
					0
				);

				if (
					!! blockNode &&
					( ! blockNode.contains( startContainer ) ||
						! blockNode.contains( endContainer ) )
				) {
					selection.removeAllRanges();
				}
			}

			return;
		}

		const { length } = multiSelectedBlockClientIds;

		if ( length < 2 ) {
			return;
		}

		// These must be in the right DOM order.
		const start = multiSelectedBlockClientIds[ 0 ];
		const end = multiSelectedBlockClientIds[ length - 1 ];

		let startNode = getBlockDOMNode( start, ownerDocument );
		let endNode = getBlockDOMNode( end, ownerDocument );

		const selection = defaultView.getSelection();
		const range = ownerDocument.createRange();

		// The most stable way to select the whole block contents is to start
		// and end at the deepest points.
		startNode = getDeepestNode( startNode, 'start' );
		endNode = getDeepestNode( endNode, 'end' );

		range.setStartBefore( startNode );
		range.setEndAfter( endNode );

		selection.removeAllRanges();
		selection.addRange( range );
	}, [
		hasMultiSelection,
		isMultiSelecting,
		multiSelectedBlockClientIds,
		selectBlock,
		selectedBlockClientId,
	] );

	const onSelectionChange = useCallback(
		( { isSelectionEnd } ) => {
			const { ownerDocument } = ref.current;
			const { defaultView } = ownerDocument;
			const selection = defaultView.getSelection();

			// If no selection is found, end multi selection and enable all rich
			// text areas.
			if ( ! selection.rangeCount || selection.isCollapsed ) {
				toggleRichText( ref.current, true );
				return;
			}

			const clientId = getBlockClientId( selection.focusNode );
			const isSingularSelection = startClientId.current === clientId;

			if ( isSingularSelection ) {
				selectBlock( clientId );

				// If the selection is complete (on mouse up), and no multiple
				// blocks have been selected, set focus back to the anchor element
				// if the anchor element contains the selection. Additionally, rich
				// text elements that were previously disabled can now be enabled
				// again.
				if ( isSelectionEnd ) {
					toggleRichText( ref.current, true );

					if ( selection.rangeCount ) {
						const {
							commonAncestorContainer,
						} = selection.getRangeAt( 0 );

						if (
							anchorElement.current.contains(
								commonAncestorContainer
							)
						) {
							anchorElement.current.focus();
						}
					}
				}
			} else {
				const startPath = [
					...getBlockParents( startClientId.current ),
					startClientId.current,
				];
				const endPath = [ ...getBlockParents( clientId ), clientId ];
				const depth = Math.min( startPath.length, endPath.length ) - 1;

				multiSelect( startPath[ depth ], endPath[ depth ] );
			}
		},
		[ selectBlock, getBlockParents, multiSelect ]
	);

	/**
	 * Handles a mouseup event to end the current mouse multi-selection.
	 */
	const onSelectionEnd = useCallback( () => {
		const { ownerDocument } = ref.current;
		const { defaultView } = ownerDocument;
		ownerDocument.removeEventListener(
			'selectionchange',
			onSelectionChange
		);
		// Equivalent to attaching the listener once.
		defaultView.removeEventListener( 'mouseup', onSelectionEnd );
		// The browser selection won't have updated yet at this point, so wait
		// until the next animation frame to get the browser selection.
		rafId.current = defaultView.requestAnimationFrame( () => {
			onSelectionChange( { isSelectionEnd: true } );
			stopMultiSelect();
		} );
	}, [ onSelectionChange, stopMultiSelect ] );

	// Only clean up when unmounting, these are added and cleaned up elsewhere.
	useEffect( () => {
		const { ownerDocument } = ref.current;
		const { defaultView } = ownerDocument;

		return () => {
			ownerDocument.removeEventListener(
				'selectionchange',
				onSelectionChange
			);
			defaultView.removeEventListener( 'mouseup', onSelectionEnd );
			defaultView.cancelAnimationFrame( rafId.current );
		};
	}, [ onSelectionChange, onSelectionEnd ] );

	/**
	 * Binds event handlers to the document for tracking a pending multi-select
	 * in response to a mousedown event occurring in a rendered block.
	 */
	return useCallback(
		( clientId ) => {
			if ( ! isSelectionEnabled ) {
				return;
			}

			const { ownerDocument } = ref.current;
			const { defaultView } = ownerDocument;

			startClientId.current = clientId;
			anchorElement.current = ownerDocument.activeElement;
			startMultiSelect();

			// `onSelectionStart` is called after `mousedown` and `mouseleave`
			// (from a block). The selection ends when `mouseup` happens anywhere
			// in the window.
			ownerDocument.addEventListener(
				'selectionchange',
				onSelectionChange
			);
			defaultView.addEventListener( 'mouseup', onSelectionEnd );

			// Removing the contenteditable attributes within the block editor is
			// essential for selection to work across editable areas. The edible
			// hosts are removed, allowing selection to be extended outside the
			// DOM element. `startMultiSelect` sets a flag in the store so the rich
			// text components are updated, but the rerender may happen very slowly,
			// especially in Safari for the blocks that are asynchonously rendered.
			// To ensure the browser instantly removes the selection boundaries, we
			// remove the contenteditable attributes manually.
			toggleRichText( ref.current, false );
		},
		[ isSelectionEnabled, startMultiSelect, onSelectionEnd ]
	);
}
